import { useCallback, useEffect, useRef } from 'react';

/**
 * Bridges Unity analytics events to GameAnalytics and Google Analytics.
 *
 * Listens for design, resource, progression, and error events from the Unity
 * context and forwards them to the provided analytics clients.
 *
 * @param ga - Initialized Google Analytics instance
 * @param gameGA - Initialized GameAnalytics instance
 * @param addEventListener - Unity context addEventListener
 * @param removeEventListener - Unity context removeEventListener
 */
export const useAnalyticsEvents = ({
    ga,
    gameGA,
    addEventListener,
    removeEventListener,
}) => {
    const gameGARef = useRef(null);
    const gaRef = useRef(null);

    gameGARef.current = gameGA;
    gaRef.current = ga;

    const handleSendDesignEvent = useCallback(
        (eventName, value = undefined, fieldsJson = undefined) => {
            gameGARef.current?.sendDesignEvent(
                eventName,
                value,
                fieldsJson,
                'Game'
            );
            gaRef.current?.basicGaEvent('game', eventName, value);
        },
        []
    );

    const handleSendResourcesEvent = useCallback(
        (flowType, currency, amount, itemType, itemId) => {
            gameGARef.current?.sendResourceEvent(
                flowType,
                currency,
                amount,
                itemType,
                itemId
            );
        },
        []
    );

    const handleSendProgressionEvent = useCallback(
        (status, progression1, progression2, progression3, score) => {
            gameGARef.current?.sendProgressionEvent(
                status,
                progression1,
                progression2,
                progression3,
                score
            );
        },
        []
    );

    const handleSendEventError = useCallback((errorType, message) => {
        gameGARef.current?.sendErrorEvent(errorType, message);
        gaRef.current?.basicGaEvent('error', message);
    }, []);

    useEffect(() => {
        // Bacanagol emits SendAnalyticsEvent; kickandboom emits SendDesignEvent.
        addEventListener('SendDesignEvent', handleSendDesignEvent);
        addEventListener('SendAnalyticsEvent', handleSendDesignEvent);
        addEventListener('SendResourcesEvent', handleSendResourcesEvent);
        addEventListener('SendProgressionEvent', handleSendProgressionEvent);
        addEventListener('SendErrorEvent', handleSendEventError);

        return () => {
            removeEventListener('SendDesignEvent', handleSendDesignEvent);
            removeEventListener('SendAnalyticsEvent', handleSendDesignEvent);
            removeEventListener('SendResourcesEvent', handleSendResourcesEvent);
            removeEventListener('SendErrorEvent', handleSendEventError);
            removeEventListener(
                'SendProgressionEvent',
                handleSendProgressionEvent
            );
        };
    }, [
        addEventListener,
        removeEventListener,
        handleSendDesignEvent,
        handleSendResourcesEvent,
        handleSendProgressionEvent,
        handleSendEventError,
    ]);
};
